import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { IWard } from './Iward';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { WardService } from './ward.service';
import { MatDialog } from '@angular/material/dialog';
import { WardDetailComponent } from './ward-detail/ward-detail.component';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';


@Component({
  selector: 'app-ward',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    ReactiveFormsModule,
    DefaultValuePipe
  ],
  templateUrl: './ward.component.html',
  styleUrl: './ward.component.scss'
})
export class WardComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() wardClick = new EventEmitter<IWard>();

  displayedColumns: string[] = [
    'name',
    'code',
    'isActive',
    'action',
  ];
  dataSource = new MatTableDataSource<IWard>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  pagination: PaginationResult;
  wardcount: number = 0;
  warddata: IWard[] = [];
  isLoading: boolean = false;
  searchWard: FormControl = new FormControl('');
  pageSizeOptions = PAGE_SIZE_OPTIONS;


  constructor(
    private _wardService: WardService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this._wardService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.wardcount = this.pagination.rowCount;
        }
      });

    this._wardService.wards$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.warddata = data;
        this.dataSource.data = data;
      });

    this.refreshWard();

    this.searchWard.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        const pageSize = this.pagination?.pageSize || 10;
        return this._wardService.getAllWards({
          pageNumber: 0,
          pageSize: pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (this.isLoading = false)),
          catchError((error) => {
            this.toastr.error('Error occurred while searching');
            console.error('Error searching wards:', error);
            return of(null);
          })
        );
      })
    ).subscribe();
  }

  onPageChange(event: any): void {
    if (this.pagination) {
      this.pagination.currentPage = event.pageIndex;
      this.pagination.pageSize = event.pageSize;
      this.refreshWard();
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onAddWard() {
    this.dialog.open(WardDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
    }).afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (result) {
          this.refreshWard();
        }
      });
  }

  onUpdateWard(element: IWard) {
    this.dialog.open(WardDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
      data:
      {
        ward: element,
        isEdit: true
      }
    }).afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (result) {
          this.refreshWard();
        }
      });
  }

  onDeleteWard(wardId: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.WARD') + ' ?' }
    })
    confirmation.afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        if (result === "confirm") {
          this._wardService.deleteWard(wardId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
              next: (response) => {
                if (response.isSuccess) {
                  this.refreshWard();
                  this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.WARD')));
                }
                else {
                  const allErrors = Object.values(response.errors).flat();
                  this.toastr.error(allErrors.join("\n"));
                }
              },
              error: (error) => {
                this.toastr.error('Error occurred while deleting');
                console.error('Error deleting ward:', error);
              }
            });
        }
      });

  }

  refreshWard() {
    this.isLoading = true;
    const globalSearchValue = this.searchWard?.value?.trim();
    const requestPayload: any = {
      pageNumber: this.pagination?.currentPage || 0,
      pageSize: this.pagination?.pageSize || 10,
      isDescending: false,
      searchValue: globalSearchValue || ''
    };

    this._wardService.getAllWards(requestPayload)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.toastr.error('Error occurred while refreshing data');
          console.error('Error refreshing wards:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  onRowClick(ward: IWard): void {
    this.wardClick.emit(ward);
    this.router.navigate(['/ipd/ward', ward.id]);
  }

  onActionClick(event: Event): void {
    event.stopPropagation();
  }

}
